import { IJSONSchema } from "../../../../base/common/jsonSchema.js";
import { isLocalizedString } from "../../../action/common/action.js";
import { CommandsRegistry, ICommandMetadata, ICommandService } from "../../../commands/common/commands.js";
import { ILogger, LogLevel } from "../../../log/common/log.js";
import { AIToolCallResponse, IAITool, isAIToolCallResponse } from "../ai.js";



export function createToolFromCommand(commandId: string, commandService: ICommandService, logger: ILogger): IAITool | null {

    const cmd = CommandsRegistry.getCommand(commandId);
    if (!cmd) {
        logger?.debug(`Command '${commandId}' not found. Skipping AI tool creation.`);
        return null;
    }

    // Check if command supports AI execution
    if (!cmd.executeForAI) {
        logger?.debug(`Command '${commandId}' does not implement executeForAI. Skipping AI tool creation.`);
        return null;
    }

    if (!cmd.metadata) {
        logger?.debug(`Command '${commandId}' has no metadata. Skipping AI tool creation.`);
        return null;
    }
    else if (!cmd.metadata.description) {
        logger?.debug(`Command '${commandId}' has no description in metadata. Skipping AI tool creation.`);
        return null;
    }
    else if (!cmd.metadata.args) {
        logger?.debug(`Command '${commandId}' has no args in metadata. Skipping AI tool creation.`);
        return null;
    }

    const metdata = cmd.metadata;

    const description: string = cmd.metadata.description
        ? isLocalizedString(cmd.metadata.description)
            ? cmd.metadata.description.value
            : cmd.metadata.description
        : `Executes the command '${commandId}'.`;

    const parametersJSONSchema = commandMetadataToJSONSchema(cmd.metadata);

    return new class implements IAITool {
        readonly type = "function";
        readonly name = commandId;
        readonly description = description;
        readonly parametersJSONSchema = parametersJSONSchema;
        readonly allowMcp = metdata.args?.allowMcp;

        async call(parameters?: any): Promise<AIToolCallResponse> {
            try {
                const res = await commandService.executeCommand(commandId, parameters, { isAi: true });
                if (!res.success) {
                    return {
                        result: {
                            success: false,
                            message: `Command '${commandId}' execution failed: ${res.message || 'No message provided'}`
                        }
                    };
                }
                return {
                    result: {
                        success: true,
                        message: res.message,
                        aiMessage: isAIToolCallResponse(res) ? res.result.aiMessage : res.result?.toString(),
                    }
                };
            }
            catch (err: any) {
                return {
                    result: {
                        success: false,
                        message: `Error executing command '${commandId}': ${err.message}`
                    }
                };
            }
        }
    }
}



/**
 * Converts command metadata arguments to a JSON schema suitable for AI tools.
 *
 * - If there's a single argument with a schema, returns that schema directly
 * - If there are multiple arguments, wraps them in an object with each argument as a property
 * - Returns an empty object schema as fallback
 */
function commandMetadataToJSONSchema(metadata?: ICommandMetadata): IJSONSchema {
    if (metadata?.args?.schema) {
        return metadata.args.schema;
    }
    // Fallback schema if command metadata is not available
    return {
        type: "object",
        properties: {}
    };
}